import * as Yup from 'yup';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs } from 'src/components/organisms/ContentCard';
import { Formik, FormikHelpers } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';

import FormHelper from 'src/utils/formHelper';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useState } from 'react';
import { useTransferOrderOptions } from 'src/hooks/forms/useOptions';
import MenuItem from '@mui/material/MenuItem';
import { useTransferOrderForm } from 'src/hooks/forms/useForms';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useSelectProductOnTransfer } from 'src/hooks/table/configs/useSelectProductOnTransfer';
import SelectTableDialog from 'src/components/organisms/tables/SelectTableDialog';
import { useTransferOrderSubmit } from 'src/hooks/forms/useTransferOrderSumbit';
import SendIcon from '@mui/icons-material/Send';
import useRequest from 'src/hooks/api/useRequest';
import { releaseTransferOrder, revertTransferOrder } from 'src/utils/apiRequests';
import UndoIcon from '@mui/icons-material/Undo';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';
import useUpdateEffect from 'src/hooks/useUpdateEffect';
import { useFormDefaults } from 'src/hooks/forms/useFormDefaults';
import { FormDefaultsMenu } from 'src/components/molecules/formik/FormDefaultsMenu';
import { useVideoDialog, VideoButton, VideoDialog } from 'src/components/molecules/video-dialog/VideoDialog';

// NEW
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const scheme = Yup.object().shape({
    DeliveryMethodId: Yup.number()
        .typeError('Delivery Method must be a number')
        .required('Delivery Method is required'),
    FromWarehouse: Yup.number()
        .typeError('From Warehouse must be a number')
        .required('From Warehouse is required'),
    ToWarehouse: Yup.number()
        .typeError('To Warehouse must be a number')
        .required('To Warehouse is required')
        .notOneOf([Yup.ref('FromWarehouse')], 'To Warehouse must be different from From Warehouse'),
});

const defaultInitialValues = {
    DeliveryMethodId: 8,//warehouse pickup point
    FromWarehouse: null,
    Status_Value: "",
};

const EditTransferOrder = () => {
    const [editId, PageName, currentSubmitFunction, , isEditPage] = FormHelper.getEditPageConfig("Transfer Order");
    const [statusChangeResult, setStatusChangeRequest] = useRequest({ logLevel: ["error", "success"] });
    const [result, submit] = useTransferOrderSubmit();
    const initialValues: any = useTransferOrderForm(editId, defaultInitialValues, statusChangeResult, result);
    const [deliverMethodOptions, warehouseOptions] = useTransferOrderOptions();
    useRedirectOnCreate(result, editId, "OrderId", "/warehouse/transfers/edit?id=")

    const release = () => { setStatusChangeRequest(releaseTransferOrder(editId)); }
    const revert = () => { setStatusChangeRequest(revertTransferOrder(editId)); }

    // EditTransferOrder.tsx (only the destructure line changes)
    //console.log("REsult", result)
    const [productColumns, productTableSettings, changeProducts, productsSelector] = useSelectProductOnTransfer(initialValues, result);

    const tabs: CardTab[] = [
        { value: 'Order Rows', label: 'Order Rows' },
    ]
    const [currentTab, setCurrentTab] = useState<string>('Order Rows');
    const videoRef = useVideoDialog("https://www.youtube.com/embed/B6CfYLnluTo?si=iwERkftvjwaLpdSa");

    const [submitting, setSubmitting] = useState(false);
    useUpdateEffect(() => {
        setSubmitting(false);
    }, [result])

    const { FormDefaultsListener, formDefaultsProps } = useFormDefaults({
        formKey: "EditTransferOrderForm",
        isEditPage,
        fields: {
            FromWarehouse: { clearValue: null },
            ToWarehouse: { clearValue: null },
            DeliveryMethodId: { fallback: 8, clearValue: 8 },
        },
    });

    return (
        <>
            <Formik
                initialValues={initialValues}
                validateOnBlur={true}
                validateOnChange={false}
                validationSchema={scheme}
                onSubmit={(values: any, formikHelpers: FormikHelpers<any>) => {
                    setSubmitting(true);
                    submit(values, changeProducts, formikHelpers);
                }}
                enableReinitialize
            >
                {(props): JSX.Element => {
                    const swapDisabled =
                        props.isSubmitting ||
                        (isEditPage && !!initialValues?.Status_Value && initialValues?.Status_Value !== "Draft"); // only enabled for Draft or "new" (no status / !isEditPage)

                    return (
                        <div>
                            <FormDefaultsListener formikProps={props} />
                            <PageHeader  {...{
                                title: PageName,
                                initialValues: initialValues,
                                subTitle: "Create and edit Transfer Order",
                                components: [
                                  { component: <VideoButton videoRef={videoRef} /> },
                                    { component: <CustomButton disabled={initialValues?.Status_Value != "Pending"} tooltipText={initialValues?.Status_Value != "Pending" ? 'Only transfer orders with status "Pending" can be reverted.' : undefined} isSubmitting={props.isSubmitting} onClick={revert} title="Revert Send" icon={<UndoIcon />} /> },
                                    { isInGroupedMenu: true, component: <FormDefaultsMenu formikProps={props} formDefaultsProps={formDefaultsProps} /> },
                                    {
                                        component: (
                                            <CustomButton
                                                disabled={
                                                    initialValues?.Status_Value != "Draft" ||
                                                    (changeProducts?.length === 0)
                                                }
                                                tooltipText={
                                                    (initialValues?.Status_Value != "Draft" || (changeProducts?.length === 0))
                                                        ? `${initialValues?.Status_Value != "Draft" ? 'Only transfer orders with status "Draft" can be sent.' : ''}${(initialValues?.Status_Value != "Draft" && (changeProducts?.length === 0)) ? ' ' : ''}${(changeProducts?.length === 0) ? 'Add at least one product row before sending.' : ''}`
                                                        : undefined
                                                }
                                                isSubmitting={props.isSubmitting}
                                                onClick={release}
                                                title="Send"
                                                icon={<SendIcon />}
                                            />
                                        )
                                    },

                                    {
                                        component: <CustomButton disabled={initialValues?.Status_Value == "Complete" || initialValues?.Status_Value == "Pending" || initialValues?.Status_Value == "InTransit"}
                                            tooltipText={(initialValues?.Status_Value == "Complete" || initialValues?.Status_Value == "Pending" || initialValues?.Status_Value == "InTransit") ? `Saving is disabled while status is "${initialValues?.Status_Value}".` : undefined}
                                            isSubmitting={submitting} onClick={() => {
                                                props.handleSubmit();
                                            }}
                                            title="Save" icon={<SaveIcon />} />
                                    },

                                ],
                                alertData: { dataArray: (result ?? false) ? [result] : null }
                            }} />
                            <VideoDialog videoRef={videoRef} />
                            <ContentCardsContainer >

                                <ContentCard {...{ cardId: "Delivery", md: 6 }}>
                                    <FormContainerMolecule formikProps={props}>
                                        <FormField fieldProps={{ name: "FromWarehouse", select: true, label: "From Warehouse", md: 5.5 }} formikProps={props}>
                                            {warehouseOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </FormField>

                                        <CustomButton
                                            iconButton
                                            size="small"
                                            icon={<SwapHorizIcon fontSize="small" />}
                                            disabled={swapDisabled}
                                            tooltipText={swapDisabled ? 'Swap is only available for new or Draft transfer orders.' : 'Swap From/To warehouses'}
                                            onClick={() => {
                                                const from = props.values.FromWarehouse;
                                                const to = props.values.ToWarehouse;
                                                props.setFieldValue('FromWarehouse', to);
                                                props.setFieldValue('ToWarehouse', from);
                                            }}
                                        />

                                        <FormField fieldProps={{ name: "ToWarehouse", select: true, label: "To Warehouse", md: 5.5 }} formikProps={props}>
                                            {warehouseOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </FormField>

                                        <FormField fieldProps={{ name: "ExternalShipmentId", label: "External Shipment Id", md: 5.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "ETA", date: true, label: "ETA", md: 5.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "DeliveryMethodId", select: true, label: "Delivery Method", md: 12 }} formikProps={props}>
                                            {deliverMethodOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </FormField>
                                    </FormContainerMolecule>
                                </ContentCard>

                                <ContentCard {...{ cardId: "Info", md: 6 }}>
                                    <FormContainerMolecule disabled={true} formikProps={props}>
                                        <FormField fieldProps={{ name: "Status_Value", label: "Status", md: 8.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "ReceivingAdviceId", label: "Receiving Advice Id", md: 2.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "CreatedBy", label: "Created By", md: 5.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "CreateDate", label: "Create Date", md: 5.8 }} formikProps={props}></FormField>
                                        <FormField fieldProps={{ name: "ModifyDate", label: "Modify Date", md: 5.8 }} formikProps={props}></FormField>
                                    </FormContainerMolecule>
                                </ContentCard>

                                <ContentCard {...{
                                    cardId: "TabContainer",
                                    contentPadding: 0,
                                    cardTypes: [CardType.tabContainer],
                                    cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} ></HeaderTabs>,
                                    components: [
                                        {
                                            component: <CustomButton disabled={(initialValues?.Status_Value != "Draft")
                                            } tooltipText={(initialValues?.Status_Value != "Draft") ? 'You can only add products while the transfer order status is "Draft".' : undefined} onClick={() => { productsSelector?.openSelector() }}
                                                title="Add Product" icon={<AddTwoToneIcon />} />
                                        },
                                    ],
                                }}>
                                    {currentTab === "Order Rows" &&
                                        <>
                                            <SelectTableDialog {...productsSelector} />
                                            <ContentCard {...{ cardId: "Order Rows", cardTypes: [CardType.tab], contentPadding: 0, }}>
                                                <CustomDataGrid columns={productColumns} tableSettings={productTableSettings} />
                                            </ContentCard>
                                        </>
                                    }
                                </ContentCard>

                            </ContentCardsContainer>
                        </div>
                    );
                }}
            </Formik>

            <Footer />
        </>
    );
}

export default EditTransferOrder;